// another function to go from string to ByteArray, but we first encode the

import { IRegisteredCredentials } from '../type';

// string as base64 - note the use of the atob() function
export const strToBin = (str: string) => {
	return Uint8Array.from(atob(str), c => c.charCodeAt(0));
};

// function to encode raw binary to string, which is subsequently
// encoded to base64 - note the use of the btoa() function

export const binToStr = (bin: any) => {
	return btoa(
		new Uint8Array(bin).reduce((s, byte) => s + String.fromCharCode(byte), '')
	);
};

export const getRegistrations = () => {
	const registrations = JSON.parse(
		localStorage.simpliciAppRegistration || '[]'
	);
	return registrations;
};

export const setRegistrations = (registrations: IRegisteredCredentials) => {
	localStorage.simpliciAppRegistration = JSON.stringify(
		registrations,
		null,
		'  '
	);
};

export const saveNewCredentials = (registration: IRegisteredCredentials) => {
	const registrations = getRegistrations();
	registrations.push(registration);
	setRegistrations(registrations);
};

/**
 * Does THIS browser hold one of the credentials the server is about to ask
 * for?
 *
 * `allowCredentials` is the account's full passkey list — every browser and
 * device it was ever registered from. `navigator.credentials.get()` with that
 * list raises the browser's native "There are no matching passkeys saved in
 * Passwords for '<host>'" sheet when none of them live here, and nothing in
 * the DOM can reach or dismiss that sheet. The only way not to show it is not
 * to call `get()`.
 *
 * `saveNewCredentials` records every credential `create()` minted in this
 * browser, so intersecting the two lists answers the question before the
 * call. It is a heuristic in one direction only: a passkey synced in through
 * iCloud Keychain from another Apple device IS usable here but unknown to
 * localStorage (false negative — the caller must still offer "try it"). A
 * stored id whose passkey was since deleted from the OS store is a false
 * positive that just falls through to `get()`'s failure path.
 */
export const hasLocalCredential = (
	allowCredentials: { id?: string }[] | undefined
): boolean => {
	if (!allowCredentials?.length) return false;
	let known: IRegisteredCredentials[] = [];
	try {
		known = getRegistrations();
	} catch {
		return false;
	}
	const localIds = new Set(
		known.flatMap(reg => [reg?.id, reg?.rawId].filter(Boolean))
	);
	return allowCredentials.some(cred => !!cred?.id && localIds.has(cred.id));
};

export const bufferToBase64URLString = (buffer: any) => {
	const bytes = new Uint8Array(buffer);
	let str = '';

	for (const charCode of bytes as any) {
		str += String.fromCharCode(charCode);
	}

	const base64String = btoa(str);

	return base64String.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
};

const attachments = ['cross-platform', 'platform'];

export const toAuthenticatorAttachment = (attachment: any) => {
	if (!attachment) {
		return;
	}

	if (attachments.indexOf(attachment) < 0) {
		return;
	}

	return attachment;
};

export const base64URLStringToBuffer = (base64URLString: any) => {
	// Convert from Base64URL to Base64
	const base64 = base64URLString.replace(/-/g, '+').replace(/_/g, '/');
	/**
	 * Pad with '=' until it's a multiple of four
	 * (4 - (85 % 4 = 1) = 3) % 4 = 3 padding
	 * (4 - (86 % 4 = 2) = 2) % 4 = 2 padding
	 * (4 - (87 % 4 = 3) = 1) % 4 = 1 padding
	 * (4 - (88 % 4 = 0) = 4) % 4 = 0 padding
	 */
	const padLength = (4 - (base64.length % 4)) % 4;
	const padded = base64.padEnd(base64.length + padLength, '=');

	// Convert to a binary string
	const binary = atob(padded);

	// Convert binary string to buffer
	const buffer = new ArrayBuffer(binary.length);
	const bytes = new Uint8Array(buffer);

	for (let i = 0; i < binary.length; i++) {
		bytes[i] = binary.charCodeAt(i);
	}

	return buffer;
};

export const utf8StringToBuffer = (value: any) => {
	return new TextEncoder().encode(value);
};
